// MACHINERY V2 (Stroje) — fleet toolbar (M2.2).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.2 + §9 D4. Reference impl:
// design_handoff_machinery_v3/machinery-shared.jsx (TabsRow, ViewModeToggle,
// MachineryShell's action row).
//
// CONTENTS: search (400 ms debounce, owned by the page) · the Aktívne /
// Vyradené scope tabs (§9 D4: the decommissioned view is CARRIED, rebuilt as a
// v2 chip pair instead of the live DecommissionViewToggle) · the group-by chips
// (M2.3, §9 D4) · the V1|V2|V3 view toggle (`setFleetView`, persisted since
// M2.13) · Spravovať skupiny · Pridať nové zariadenie.
//
// THE GROUP-BY CHIPS SERVE BOTH VIEWS (M2.3 ruling (i)). `fleetGroupBy` is the
// single, client-side grouping key: useFleetPageData sections the filtered base
// set by it once, and V1's cards and V2's rows both render that same map. They
// were briefly V2-only, while V1 still showed the SERVER's grouping — that
// second key is now display-inert (see the note at `fetchQuery`).
//
// TWO RULINGS MEET IN THE VIEW TOGGLE, AND THEY ARE DIFFERENT THINGS. This
// comment used to read "ONLY TWO VIEW SIZES ... Do not add a third", which was
// true for its era but collapsed a card SIZE ruling and a view ruling into one
// sentence — so it now reads as forbidding what M2.13/M2.14 authorise. Both
// sources, kept separate:
//   · §9 D4 — the compact third CARD VARIANT and `CardLayoutToggle` are DROPPED.
//     PERMANENT, STILL IN FORCE: there is no compact card size, no card-size
//     control, and none may be added here.
//   · Ticket rows M2.13 / M2.14 — the V3 dense TABLE and the V4 shop-floor WALL
//     are AUTHORISED additions to this toggle. BOTH HAVE NOW LANDED: V3 at
//     M2.13, V4 at M2.14.
// A card size is how one card is drawn; a view is which body renders the fleet.
// The handoff's four-way ViewModeToggle (big/med/table/wall) is therefore
// reproduced in full as VIEWS — V1 · V2 · V3 · V4 — while D4's ban on a third
// card size stands untouched.
//
// V4 IS THE ONE VIEW THAT UNMOUNTS THIS TOOLBAR. Picking the wall puts the page
// into kiosk layout (no rail, no toolbar — ticket M2.14 / README §V4), so
// FleetPage branches away from this component entirely — including away from
// the toggle that got the user there. The wall button was therefore a ONE-WAY
// DOOR until the M2.14 fix pass gave the kiosk three ways back (Esc, `?view=`,
// and a pointer-only hint chip); it is still the only control in this toolbar
// that unmounts the toolbar. See FleetPage's kiosk-branch header.
//
// PERMISSIONS (§10): Spravovať skupiny and Pridať nové zariadenie are Write —
// a Worker sees neither. Search, the scope tabs and the view toggle are Read.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2FleetView, MachineryV2GroupBy } from "../machineryV2UiSlice";
import { FLEET_GROUP_BY_OPTIONS } from "./fleetGroupBy";

const SearchGlyph: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
    <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" />
    <line x1="8.5" y1="8.5" x2="11.5" y2="11.5" stroke="currentColor" strokeLinecap="round" />
  </svg>
);

const BigGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <rect x="1" y="1" width="5.5" height="5.5" rx="1" stroke="currentColor" />
    <rect x="7.5" y="1" width="5.5" height="5.5" rx="1" stroke="currentColor" />
    <rect x="1" y="7.5" width="5.5" height="5.5" rx="1" stroke="currentColor" />
    <rect x="7.5" y="7.5" width="5.5" height="5.5" rx="1" stroke="currentColor" />
  </svg>
);

const RowsGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <rect x="1" y="1.5" width="12" height="3.5" rx="1" stroke="currentColor" />
    <rect x="1" y="6" width="12" height="3.5" rx="1" stroke="currentColor" />
    <rect x="1" y="10.5" width="12" height="2" rx="1" stroke="currentColor" />
  </svg>
);

const TableGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <rect x="1" y="1.5" width="12" height="11" rx="1" stroke="currentColor" />
    <line x1="1" y1="5" x2="13" y2="5" stroke="currentColor" />
    <line x1="1" y1="9" x2="13" y2="9" stroke="currentColor" />
    <line x1="6" y1="1.5" x2="6" y2="12.5" stroke="currentColor" />
  </svg>
);

/** The wall: a screen on a stand. Deliberately unlike the four-pane BigGlyph —
 *  at 14px the two must not read as the same icon. */
const WallGlyph: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
    <rect x="1" y="1.5" width="12" height="8.5" rx="1" stroke="currentColor" />
    <line x1="7" y1="10" x2="7" y2="12.5" stroke="currentColor" />
    <line x1="4.5" y1="12.5" x2="9.5" y2="12.5" stroke="currentColor" strokeLinecap="round" />
    <rect x="3" y="3.5" width="3.5" height="2.5" rx="0.5" fill="currentColor" />
  </svg>
);

const Chip: React.FC<{
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}> = ({ active, label, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      height: 32, padding: "0 12px", borderRadius: 999, border: 0, cursor: "pointer",
      fontFamily: "inherit", fontSize: 13, display: "inline-flex", alignItems: "center",
      gap: 8, background: active ? "var(--mod-accent)" : "transparent",
      color: active ? "var(--mod-accent-fg)" : "var(--muted)",
      fontWeight: active ? 600 : 500, whiteSpace: "nowrap",
    }}
  >
    <span>{label}</span>
    {count !== undefined && (
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 11, opacity: active ? 0.9 : 0.7 }}>
        {count}
      </span>
    )}
  </button>
);

export type FleetScope = "active" | "decommissioned";

export interface FleetToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;
  scope: FleetScope;
  onScopeChange: (scope: FleetScope) => void;
  activeCount: number;
  decommissionedCount: number;
  view: MachineryV2FleetView;
  onViewChange: (view: MachineryV2FleetView) => void;
  groupBy: MachineryV2GroupBy;
  onGroupByChange: (groupBy: MachineryV2GroupBy) => void;
  canWrite: boolean;
  onManageGroups: () => void;
  onAddMachine: () => void;
}

const FleetToolbar: React.FC<FleetToolbarProps> = ({
  search, onSearchChange, onSearchSubmit, scope, onScopeChange, activeCount,
  decommissionedCount, view, onViewChange, groupBy, onGroupByChange, canWrite,
  onManageGroups, onAddMachine,
}) => {
  const { translate } = useTranslation("myMachinery");

  const viewButton = (value: MachineryV2FleetView, glyph: React.ReactNode, label: string) => {
    const active = view === value;
    return (
      <button
        type="button"
        onClick={() => onViewChange(value)}
        title={label}
        aria-label={label}
        aria-pressed={active}
        style={{
          width: 30, height: 26, padding: 0, borderRadius: 6, border: 0,
          cursor: "pointer", display: "grid", placeItems: "center",
          background: active ? "var(--card)" : "transparent",
          color: active ? "var(--mod-accent)" : "var(--muted)",
          boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
        }}
      >
        {glyph}
      </button>
    );
  };

  return (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
        padding: "14px 24px 12px",
      }}
    >
      <div
        style={{
          height: 32, padding: "0 10px", background: "var(--card)",
          border: "1px solid var(--border)", borderRadius: 8, display: "flex",
          alignItems: "center", gap: 8, color: "var(--faint)", flex: "1 1 240px",
          minWidth: 200, maxWidth: 360,
        }}
      >
        <SearchGlyph />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") onSearchSubmit();
          }}
          placeholder={translate("myMachinery:searchByMachineModel")}
          style={{
            flex: 1, minWidth: 0, border: 0, outline: "none", background: "transparent",
            fontFamily: "inherit", fontSize: 12.5, color: "var(--ink)",
          }}
        />
      </div>

      <Chip
        active={scope === "active"}
        label={translate("myMachinery:activeView")}
        count={activeCount}
        onClick={() => onScopeChange("active")}
      />
      <Chip
        active={scope === "decommissioned"}
        label={translate("myMachinery:decommissionedView")}
        count={decommissionedCount}
        onClick={() => onScopeChange("decommissioned")}
      />

      <span style={{ width: 1, height: 18, background: "var(--border)", margin: "0 4px" }} />
      <span className="eyebrow" style={{ marginRight: 2 }}>
        {translate("myMachinery:v2.fleet.groupBy")}
      </span>
      {FLEET_GROUP_BY_OPTIONS.map((o) => (
        <Chip
          key={o.key}
          active={groupBy === o.key}
          label={translate(o.labelKey)}
          onClick={() => onGroupByChange(o.key)}
        />
      ))}

      <span style={{ flex: 1 }} />

      <div
        style={{
          display: "inline-flex", gap: 2, padding: 2, background: "var(--card-2)",
          border: "1px solid var(--border)", borderRadius: 8,
        }}
      >
        {/* Order is the designer's: big cards · rows · table · wall. */}
        {viewButton("V1", <BigGlyph />, translate("myMachinery:v2.fleet.viewBigCards"))}
        {viewButton("V2", <RowsGlyph />, translate("myMachinery:v2.fleet.viewGrouped"))}
        {viewButton("V3", <TableGlyph />, translate("myMachinery:v2.fleet.viewTable"))}
        {viewButton("V4", <WallGlyph />, translate("myMachinery:v2.fleet.viewWall"))}
      </div>

      {canWrite && (
        <>
          <button type="button" className="btn" onClick={onManageGroups}>
            <span
              style={{
                fontFamily: "var(--font-mono)", fontSize: 10, letterSpacing: "0.08em",
                textTransform: "uppercase",
              }}
            >
              {translate("myMachinery:manageGroups")}
            </span>
          </button>
          <button
            type="button"
            className="btn btn-mod"
            onClick={onAddMachine}
            style={{ height: 32 }}
          >
            <span style={{ fontSize: 15, lineHeight: 1, marginRight: 2 }}>+</span>
            {translate("myMachinery:addNewMachine")}
          </button>
        </>
      )}
    </div>
  );
};

export default FleetToolbar;
